import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { Link } from "react-router";

import ChartDot from "../molecules/ChartDot";
import { InfoIconButton } from "../molecules/InfoIconButton";
import Line, { type Position } from "../molecules/Line";
import { type DocumentVersionSummary } from "~/api/types";
import { useTranslation } from "react-i18next";
import DocumentIcon from "../molecules/DocumentIcon";
import { formatDate, isPastDate } from "~/helpers/date";
import cn from "classnames";
import { GripVertical } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/atoms/tooltip";

interface ChartReorderContextValue {
  reorderKey: number;
  notifyReorderStart: () => void;
}

export const ChartReorderContext = createContext<ChartReorderContextValue>({
  reorderKey: 0,
  notifyReorderStart: () => undefined,
});

interface ChartCrossCategoryContextValue {
  documentVersions: DocumentVersionSummary[];
  showCrossCategoryLinksTooltip: boolean;
}

export const ChartCrossCategoryContext =
  createContext<ChartCrossCategoryContextValue>({
    documentVersions: [],
    showCrossCategoryLinksTooltip: false,
  });

const CrossCategoryTooltip = ({
  documents,
}: {
  documents: DocumentVersionSummary[];
}) => {
  const { t } = useTranslation();
  if (documents.length === 0) return null;

  const groups = new Map<
    number,
    { title: string; docs: DocumentVersionSummary[] }
  >();
  for (const v of documents) {
    const { id, title } = v.document.category;
    const group = groups.get(id) ?? { title, docs: [] };
    group.docs.push(v);
    groups.set(id, group);
  }

  return (
    <TooltipContent className="flex flex-col gap-2 p-2">
      <div className="text-xs font-medium">
        {t(($) => $.chart.linkedDocumentsOutsideCategory)}
      </div>
      <div className="flex flex-col gap-2">
        {Array.from(groups.values()).map((group) => (
          <div key={group.title} className="flex flex-col gap-1">
            <div className="text-muted-foreground text-xs font-semibold">
              {group.title}
            </div>
            <ul className="list-disc pl-4">
              {group.docs.map((v) => (
                <li key={v.id} className="text-muted-foreground text-xs">
                  {v.title}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </TooltipContent>
  );
};

interface Props {
  documentVersion: DocumentVersionSummary;
  documentRefsMap: React.RefObject<Map<number, HTMLDivElement>>;
  hoveredId?: number;
  setHoveredId: React.Dispatch<React.SetStateAction<number | undefined>>;
  setSelectedId: (id: number) => void;
  isDragging?: boolean;
  handleRef?: (element: Element | null) => void;
}

interface PositionWithId extends Position {
  toId: number;
}

interface DocumentLinesProps {
  documentVersion: DocumentVersionSummary;
  documentRefsMap: React.RefObject<Map<number, HTMLDivElement>>;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  hoveredId?: number;
  documents: DocumentVersionSummary[];
}

export const DocumentLines = ({
  documentVersion,
  documentRefsMap,
  containerRef,
  hoveredId,
  documents,
}: DocumentLinesProps) => {
  const { reorderKey } = useContext(ChartReorderContext);
  const [positions, setPositions] = useState<PositionWithId[]>([]);
  const { parentDocumentIds } = documentVersion.document;

  useEffect(() => {
    const measurePositions = () => {
      const newPositions: PositionWithId[] = [];
      const refsMap = documentRefsMap.current;

      parentDocumentIds.forEach((parentId) => {
        const childRef = refsMap?.get(documentVersion.document.id);
        const parentRef = refsMap?.get(parentId);

        if (childRef && parentRef && containerRef?.current) {
          const parent = parentRef.getBoundingClientRect();
          const child = childRef.getBoundingClientRect();
          const container = containerRef.current.getBoundingClientRect();

          newPositions.push({
            parentX: parent.left - container.left,
            parentY: parent.bottom - container.top,
            childX: child.left - container.left,
            childY: child.top - container.top,
            toId: parentId,
          });
        }
      });

      setPositions(newPositions);
    };

    // After a reorder, dnd-kit's drop animation takes ~250ms while the
    // dragged card remains in the browser's popover top layer. The
    // Feedback plugin cleanup (which restores the card to normal document
    // flow) only runs after the animation finishes. Delay measurement so
    // we read final flow positions instead of popover-layer positions.
    if (reorderKey) {
      const timer = setTimeout(measurePositions, 300);
      return () => clearTimeout(timer);
    }

    measurePositions();
  }, [
    containerRef,
    parentDocumentIds,
    documentVersion.document.id,
    documentRefsMap,
    documents,
    reorderKey,
  ]);

  const isHovering = hoveredId === documentVersion.document.id;

  return (
    <>
      {positions.map(({ toId, ...position }) => (
        <Line
          key={toId}
          {...position}
          offsetX={75}
          active={isHovering || hoveredId === toId}
        />
      ))}
    </>
  );
};

export const DocumentCard = ({
  documentVersion,
  documentRefsMap,
  hoveredId,
  setHoveredId,
  setSelectedId,
  isDragging,
  handleRef,
}: Props) => {
  const { t } = useTranslation();

  const { parentDocumentIds, childDocumentIds } = documentVersion.document;

  const isHovering = hoveredId === documentVersion.document.id;
  const isHoveringParent = parentDocumentIds.some((id) => hoveredId === id);
  const isHoveringChild = childDocumentIds.some((id) => hoveredId === id);

  const { documentVersions, showCrossCategoryLinksTooltip } = useContext(
    ChartCrossCategoryContext,
  );
  const myCategoryId = documentVersion.document.category.id;
  const fullVersion = showCrossCategoryLinksTooltip
    ? documentVersions.find(
        (v) => v.document.id === documentVersion.document.id,
      )
    : undefined;
  const fullParentIds = fullVersion?.document.parentDocumentIds ?? [];
  const fullChildIds = fullVersion?.document.childDocumentIds ?? [];
  const crossCategoryParents = showCrossCategoryLinksTooltip
    ? documentVersions.filter(
        (v) =>
          fullParentIds.includes(v.document.id) &&
          v.document.category.id !== myCategoryId,
      )
    : [];
  const crossCategoryChildren = showCrossCategoryLinksTooltip
    ? documentVersions.filter(
        (v) =>
          fullChildIds.includes(v.document.id) &&
          v.document.category.id !== myCategoryId,
      )
    : [];

  const dateAttribute = isPastDate(documentVersion.adoptedAt)
    ? "adoptedAt"
    : "publishedAt";

  const formattedDate = formatDate(documentVersion[dateAttribute]);

  const dateLabel =
    dateAttribute === "adoptedAt"
      ? t(($) => $.commons.document.adopted)
      : t(($) => $.commons.documentStatus.published);

  return (
    <TooltipProvider>
      <div
        ref={(node) => {
          if (node) {
            documentRefsMap.current?.set(documentVersion.document.id, node);
          } else {
            documentRefsMap.current?.delete(documentVersion.document.id);
          }
        }}
        onMouseEnter={() => setHoveredId(documentVersion.document.id)}
        onMouseLeave={() => setHoveredId(undefined)}
        className={cn(
          "relative size-37.5",
          isDragging &&
            "border-success/50 scale-[0.97] rounded-sm border-2 border-dashed opacity-50",
        )}
      >
        {(parentDocumentIds.length > 0 || crossCategoryParents.length > 0) && (
          <div className="absolute -top-1.5 left-1/2 z-10 -translate-x-1/2">
            <Tooltip>
              <TooltipTrigger asChild>
                <ChartDot
                  hasDot={parentDocumentIds.length > 0}
                  active={isHovering || isHoveringParent}
                  count={crossCategoryParents.length}
                />
              </TooltipTrigger>
              <CrossCategoryTooltip documents={crossCategoryParents} />
            </Tooltip>
          </div>
        )}

        {handleRef && (
          <span
            ref={handleRef}
            className="text-muted-foreground/60 hover:text-muted-foreground absolute top-2 left-2 z-10 cursor-grab"
            aria-hidden="true"
          >
            <GripVertical className="size-4" />
          </span>
        )}

        <div className="absolute top-2 right-2">
          <InfoIconButton
            onClick={() => setSelectedId(documentVersion.document.id)}
          />
        </div>
        <Link
          to={`/documents/${documentVersion.document.id}?versionId=${documentVersion.id}&tab=metadata`}
          className={cn(
            "bg-card hover:border-chart-1 flex h-full cursor-pointer flex-col rounded-sm border-2 border-transparent p-2 shadow-md",
            isDragging && "pointer-events-none",
          )}
        >
          <div className="size-12 p-2.5">
            <DocumentIcon type={documentVersion.fileType} />
          </div>
          <div className="text-secondary-foreground line-clamp-4 flex-1 overflow-hidden text-xs font-medium wrap-break-word hyphens-auto">
            {documentVersion.title}
          </div>
          <div className="text-muted-foreground text-[11px]">
            {`${dateLabel} ${formattedDate}`}
          </div>
        </Link>
        {(childDocumentIds.length > 0 || crossCategoryChildren.length > 0) && (
          <div className="absolute -bottom-3 left-1/2 z-10 -translate-x-1/2">
            <Tooltip>
              <TooltipTrigger asChild>
                <ChartDot
                  hasDot={childDocumentIds.length > 0}
                  active={isHovering || isHoveringChild}
                  count={crossCategoryChildren.length}
                />
              </TooltipTrigger>
              <CrossCategoryTooltip documents={crossCategoryChildren} />
            </Tooltip>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};
